import React, { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaCheck, FaRegEye } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { HiXMark } from "react-icons/hi2";
import PageHeading from "../PageHeading";
import ConfirmActionModal from "../ConfirmDeleteModal";
import Skeleton from "react-loading-skeleton";
import TableSkeleton from "../TableSkeleton";

const ViewContactModal = ({ isOpen, onClose, contact }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-2">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-xl">
        <div className="flex justify-between items-center mb-4 border-b border-dashed border-M-text-color/50 pt-6 pb-4 px-6">
          <h2 className="text-xl font-bold text-gray-700">View Contact</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <HiXMark className="w-6 h-6" />
          </button>
        </div>
        <div className="pb-6 pt-2 px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <label className="label">ID</label>
              <input
                type="text"
                value={contact?.id != null ? String(contact.id).slice(0, 8) : "N/A"}
                className="input-field bg-gray-100 cursor-not-allowed"
                disabled
              />
            </div>
            <div>
              <label className="label">Name</label>
              <input
                type="text"
                value={contact?.name || "N/A"}
                className="input-field bg-gray-100 cursor-not-allowed"
                disabled
              />
            </div>
            <div>
              <label className="label">Email</label>
              <input
                type="email"
                value={contact?.email || "N/A"}
                className="input-field bg-gray-100 cursor-not-allowed"
                disabled
              />
            </div>
            <div>
              <label className="label">Phone</label>
              <input
                type="text"
                value={contact?.phone || "N/A"}
                className="input-field bg-gray-100 cursor-not-allowed"
                disabled
              />
            </div>
            <div>
              <label className="label">Subject</label>
              <input
                type="text"
                value={contact?.subject || "N/A"}
                className="input-field bg-gray-100 cursor-not-allowed"
                disabled
              />
            </div>
            <div>
              <label className="label">Message</label>
              <textarea
                value={contact?.message || "N/A"}
                className="input-field bg-gray-100 cursor-not-allowed"
                disabled
              />
            </div>
          </div>
          <div className="flex justify-end mt-3">
            <button
              type="button"
              onClick={onClose}
              className="bg-[#E7633D]/90 text-xs md:text-sm text-white py-2 px-4 sm:px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Contact = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [isHeaderChecked, setIsHeaderChecked] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);

  const itemsPerPage = 10;
  const API_URL = "https://api.muktihospital.com";
  const API_KEY =
    "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

  // Fetch contact data
  const fetchContacts = async () => {
  setLoading(true);
  try {
    const response = await fetch(`${API_URL}/api/contact`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": API_KEY,
      },
    });

    // console.log("API Response Status:", response.status);
    const result = await response.json();
    // console.log("API Response Data:", result);

    if (response.ok) {
      const sortedContacts = [...(result.contacts || [])].sort((a, b) => {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });
      setContacts(sortedContacts);
      setSelectedRows(new Array(sortedContacts.length).fill(false));
      setError(null);
    } else {
      const errorMessage =
        result.message || `Failed to fetch contact data (Status: ${response.status})`;
      setError(errorMessage);
      toast.error(errorMessage);
    }
  } catch (err) {
    console.error("Fetch Error:", err);
    const errorMessage =
      err.name === "TypeError" && err.message.includes("Failed to fetch")
        ? "Network error: Unable to reach the server."
        : "An error occurred while fetching contact data.";
    setError(errorMessage);
    toast.error(errorMessage);
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    fetchContacts();
  }, []);

  // Filter contacts based on search term
  const filterContacts = () => {
    return contacts.filter((contact) => {
      const name = contact.name?.toLowerCase() || "";
      const email = contact.email?.toLowerCase() || "";
      const phone = contact.phone?.toLowerCase() || "";
      const subject = contact.subject?.toLowerCase() || "";
      const message = contact.message?.toLowerCase() || "";
      return (
        name.includes(searchTerm.toLowerCase()) ||
        email.includes(searchTerm.toLowerCase()) ||
        phone.includes(searchTerm.toLowerCase()) ||
        subject.includes(searchTerm.toLowerCase()) ||
        message.includes(searchTerm.toLowerCase())
      );
    });
  };

  // Pagination logic
  const filteredData = filterContacts();
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentData = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  // Debug state changes
  useEffect(() => {
    // console.log("Contacts updated:", contacts);
    // console.log("Current Page:", currentPage);
    // console.log("Filtered Data:", filteredData);
    // console.log("Total Pages:", totalPages);
    // console.log("Current Data:", currentData);
  }, [contacts, currentPage, filteredData, totalPages, currentData]);

  // Adjust page if currentPage exceeds totalPages
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      // console.log(`Adjusting page from ${currentPage} to ${totalPages}`);
      setCurrentPage(totalPages);
    } else if (totalPages === 0) {
      // console.log("No data, resetting to page 1");
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  // Update header checkbox state
  useEffect(() => {
    if (currentData.length > 0) {
      setIsHeaderChecked(
        currentData.every((_, idx) => {
          const globalIndex = contacts.findIndex(
            (contact) => contact.id === currentData[idx].id
          );
          return globalIndex !== -1 && selectedRows[globalIndex];
        })
      );
    } else {
      setIsHeaderChecked(false);
    }
  }, [selectedRows, currentData, contacts]);

  // Reset selected rows when contacts change
  useEffect(() => {
    setSelectedRows(new Array(contacts.length).fill(false));
    setIsHeaderChecked(false);
  }, [contacts]);

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      // console.log(`Changing page to ${page}`);
      setCurrentPage(page);
    }
  };

  const handleHeaderCheckboxChange = () => {
    const newSelectedRows = [...selectedRows];
    currentData.forEach((_, idx) => {
      const globalIndex = contacts.findIndex(
        (contact) => contact.id === currentData[idx].id
      );
      if (globalIndex !== -1) {
        newSelectedRows[globalIndex] = !isHeaderChecked;
      }
    });
    setSelectedRows(newSelectedRows);
    setIsHeaderChecked(!isHeaderChecked);
  };

  const handleRowCheckboxChange = (globalIndex) => {
    const newSelectedRows = [...selectedRows];
    newSelectedRows[globalIndex] = !newSelectedRows[globalIndex];
    setSelectedRows(newSelectedRows);
  };

  // Delete single or multiple contacts
  const handleDeleteContact = async (ids) => {
    const idArray = Array.isArray(ids) ? ids : [ids];
    if (idArray.length === 0) return;

    // console.log("Deleting contacts with IDs:", idArray);

    try {
      // Perform the delete requests
      const results = await Promise.allSettled(
        idArray.map((id) =>
          fetch(`${API_URL}/api/contact/${id}`, {
            method: "DELETE",
            headers: {
              "Content-Type": "application/json",
              "x-api-key": API_KEY,
            },
          })
        )
      );

      // Check for failed delete requests
      const failedRequests = results.filter(
        (result) => result.status === "rejected" || !result.value.ok
      );

      if (failedRequests.length > 0) {
        failedRequests.forEach((result, index) => {
          console.error(`Delete failed for ID ${idArray[index]}:`, result.reason);
        });
        throw new Error("Some delete requests failed.");
      }

      // Optimistically update contacts
      const updatedContacts = contacts.filter(
        (contact) => !idArray.includes(String(contact.id))
      );
      // console.log("Updated contacts:", updatedContacts);

      // Calculate new total pages based on filtered data
      const newFilteredData = updatedContacts.filter((contact) => {
        const name = contact.name?.toLowerCase() || "";
        const email = contact.email?.toLowerCase() || "";
        const phone = contact.phone?.toLowerCase() || "";
        const subject = contact.subject?.toLowerCase() || "";
        const message = contact.message?.toLowerCase() || "";
        return (
          name.includes(searchTerm.toLowerCase()) ||
          email.includes(searchTerm.toLowerCase()) ||
          phone.includes(searchTerm.toLowerCase()) ||
          subject.includes(searchTerm.toLowerCase()) ||
          message.includes(searchTerm.toLowerCase())
        );
      });
      const newTotalPages = Math.ceil(newFilteredData.length / itemsPerPage);

      // Adjust current page
      let newCurrentPage = currentPage;
      if (currentPage > newTotalPages && newTotalPages > 0) {
        newCurrentPage = newTotalPages;
      } else if (newTotalPages === 0) {
        newCurrentPage = 1;
      }

      // Update state
      setContacts([...updatedContacts]); // Ensure new array reference
      setCurrentPage(newCurrentPage);
      setSelectedRows(new Array(updatedContacts.length).fill(false));
      setIsHeaderChecked(false);

      toast.success(
        `Contact${idArray.length > 1 ? "s" : ""} deleted successfully`
      );

      // Fallback: Refetch data to ensure consistency
      await fetchContacts();
    } catch (err) {
      console.error("Delete error:", err);
      toast.error("Failed to delete contact(s). Refetching data...");
      await fetchContacts(); // Refetch on error to ensure state consistency
    } finally {
      closeDeleteModal();
    }
  };

  const openDeleteModal = (contactIds) => {
    setContactToDelete(contactIds);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setContactToDelete(null);
  };

  const openViewModal = (contact) => {
    setSelectedContact(contact);
    setViewModalOpen(true);
  };

  const closeViewModal = () => {
    setViewModalOpen(false);
    setSelectedContact(null);
  };

  const breadcrumbs = [
    { label: "Dashboard", url: "/" },
    { label: "Contact Menu" },
    { label: "Contact Submissions" },
  ];

  // TableSkeleton column configuration
  const skeletonColumns = [
    { width: 20, height: 20, isCircle: false, className: "pl-3" },
    { width: 80, height: 20, className: "px-3" },
    { width: 150, height: 20, className: "px-3" },
    { width: 200, height: 20, className: "px-3" },
    { width: 120, height: 20, className: "px-3" },
    { width: 150, height: 20, className: "px-3" },
    { width: 250, height: 20, className: "px-3" },
    { width: 250, height: 20, className: "px-3" },
    { width: 80, height: 20, className: "px-3 text-center" },
  ];

  return (
    <div>
      <ToastContainer position="top-right" />
      <ConfirmActionModal
        isOpen={deleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteContact}
        ids={contactToDelete}
        entityName="contact"
        title="Confirm Deletion"
        confirmText="Delete"
        cancelText="Cancel"
      />
      <ViewContactModal
        isOpen={viewModalOpen}
        onClose={closeViewModal}
        contact={selectedContact}
      />
      <PageHeading title="Contact Submissions" breadcrumbs={breadcrumbs} />
      {error && <div className="text-red-600 mb-4">{error}</div>}
      <div className="bg-white shadow-sm rounded-lg">
        <div className="flex flex-col sm:flex-row sm:items-center flex-wrap gap-3 justify-between border-b border-dashed border-M-text-color/50 p-4">
          <div className="inline-flex items-center gap-3">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
              Contact List
            </h2>
            {selectedRows.some((isSelected) => isSelected) && (
              <button
                onClick={() => {
                  const selectedContactIds = contacts
                    .map((contact, index) =>
                      selectedRows[index] ? contact.id : null
                    )
                    .filter((id) => id !== null);
                  openDeleteModal(selectedContactIds);
                }}
                className="size-7 bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white transition-all duration-300 text-base rounded-md inline-flex items-center justify-center"
                title="Delete selected rows"
              >
                <RiDeleteBin6Line />
              </button>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-end gap-3">
            {loading ? (
              <Skeleton height={40} width={192} />
            ) : (
              <input
                type="text"
                className="max-w-48 w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-M-heading-color shrink-0"
                placeholder="Search by name, email, phone, subject, or message"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            )}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-[1200px] w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gray-100">
                <th className="text-left">
                  <div className="relative left-3 -top-[3px]">
                    <input
                      type="checkbox"
                      id="header-checkbox"
                      className="hidden peer"
                      checked={isHeaderChecked}
                      onChange={handleHeaderCheckboxChange}
                    />
                    <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0">
                      <FaCheck className="text-slate-100" />
                    </span>
                    <label


                      htmlFor="header-checkbox"
                      className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6"
                    ></label>
                  </div>
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                  Sl
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                  ID
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                  Name
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                  Email
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                  Phone
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                  Subject
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                  Message
                </th>
                <th className="text-center uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <TableSkeleton
                  rowCount={itemsPerPage}
                  columns={skeletonColumns}
                  baseColor="#e5e7eb"
                  highlightColor="#f3f4f6"
                />
              ) : currentData.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center p-4">
                    No contact submissions available
                  </td>
                </tr>
              ) : (
                currentData.map((contact, index) => {
                  const serialNumber = index + 1;
                  const contactId =
                    contact.id != null ? String(contact.id) : "N/A";
                  const globalIndex = contacts.findIndex(
                    (c) => c.id === contact.id
                  );
                  return (
                    <tr key={contactId} className="border-b hover:bg-gray-50">
                      <td className="text-left">
                        <div className="relative left-3 -top-[3px]">
                          <input
                            type="checkbox"
                            id={`contact-${globalIndex}`}
                            className="hidden peer"
                            checked={selectedRows[globalIndex] || false}
                            onChange={() => handleRowCheckboxChange(globalIndex)}
                          />
                          <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0">
                            <FaCheck className="text-slate-100" />
                          </span>
                          <label
                            htmlFor={`contact-${globalIndex}`}
                            className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6"
                          ></label>
                        </div>
                      </td>
                      <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                        {serialNumber}
                      </td>
                      <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                        {contactId !== "N/A" ? contactId.slice(0, 8) : "N/A"}
                      </td>
                      <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                        {contact.name || "N/A"}
                      </td>
                      <td className="text-left lowercase font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                        {contact.email || "N/A"}
                      </td>
                      <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                        {contact.phone || "N/A"}
                      </td>
                      <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                        {contact.subject || "N/A"}
                      </td>
                      <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3 max-w-xs truncate">
                        {contact.message || "N/A"}
                      </td>
                      <td className="p-3 text-center space-x-2">
                        <button
                          onClick={() => openViewModal(contact)}
                          className="size-7 rounded-full bg-M-primary-color/10 text-M-primary-color text-sm inline-flex items-center justify-center hover:bg-M-primary-color hover:text-white transition-all duration-300"
                          title="View"
                        >
                          <FaRegEye />
                        </button>
                        <button
                          onClick={() => openDeleteModal([contactId])}
                          className="size-7 rounded-full bg-M-secondary-color/10 text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                          title="Delete"
                        >
                          <RiDeleteBin6Line />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="p-5 text-end">
            <ul className="inline-flex items-center justify-end border-collapse border border-M-text-color/20 rounded-md overflow-hidden">
              <li>
                <button
                  className="table-pagination-item disabled:opacity-50"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <MdKeyboardArrowLeft />
                </button>
              </li>
              {totalPages <= 10
                ? [...Array(totalPages)].map((_, i) => (
                    <li key={i}>
                      <button
                        className={`table-pagination-item ${
                          currentPage === i + 1
                            ? "bg-M-primary-color text-white"
                            : ""
                        }`}
                        onClick={() => handlePageChange(i + 1)}
                      >
                        {i + 1}
                      </button>
                    </li>
                  ))
                : (() => {
                    const pages = [];
                    const firstPages = 5;
                    const lastPages = 3;

                    for (
                      let i = 1;
                      i <= Math.min(firstPages, totalPages);
                      i++
                    ) {
                      pages.push(
                        <li key={i}>
                          <button
                            className={`table-pagination-item ${
                              currentPage === i
                                ? "bg-M-primary-color text-white"
                                : ""
                            }`}
                            onClick={() => handlePageChange(i)}
                          >
                            {i}
                          </button>
                        </li>
                      );
                    }

                    if (totalPages > firstPages + lastPages) {
                      pages.push(
                        <li key="ellipsis" className="table-pagination-item">
                          ...
                        </li>
                      );
                    }

                    for (
                      let i = Math.max(
                        firstPages + 1,
                        totalPages - lastPages + 1
                      );
                      i <= totalPages;
                      i++
                    ) {
                      pages.push(
                        <li key={i}>
                          <button
                            className={`table-pagination-item ${
                              currentPage === i
                                ? "bg-M-primary-color text-white"
                                : ""
                            }`}
                            onClick={() => handlePageChange(i)}
                          >
                            {i}
                          </button>
                        </li>
                      );
                    }

                    return pages;
                  })()}
              <li>
                <button
                  className="table-pagination-item"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  <MdKeyboardArrowRight />
                </button>
              </li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default Contact;